import React, { Component } from 'react';
import { Button, FlexColumn, Heading, Icon, Text } from 'core/components';
import { Field, InputGroup, RadioGroup, Select } from 'core/form';
import { SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import GlobalAclsPopover from './GlobalAclsPopover';

const ACLRuleRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 4px;
  padding: 2px;
  border-radius: 4px;
  background-color: ${({ theme }) => (theme.name === 'light' ? '#f5f5f5' : 'rgba(255, 255, 255, 0.05)')};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => (theme.name === 'light' ? '#e0e0e0' : 'rgba(255, 255, 255, 0.1)')};
  }
`;

const DragHandle = styled.div`
  display: flex;
  align-items: center;
  cursor: grab;
  color: ${({ theme }) => theme.colors.muted};
  padding: 4px;

  &:active {
    cursor: grabbing;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const RuleContainer = styled(FlexColumn)`
  margin-top: 16px;
  padding: 16px;
  border: ${({ theme }) => theme.borders.thinLight};
  border-radius: 4px;
  background-color: ${({ theme }) => (theme.name === 'light' ? '#f5f5f5' : 'rgba(255, 255, 255, 0.05)')};
`;

const matchTypeOptions = [
  { label: 'Exact', value: 'exact' },
  { label: 'Contains', value: 'contains' },
  { label: 'Regex', value: 'regex' }
];

const matchTypePlaceholders = {
  exact: 'e.g., show running-config',
  contains: 'e.g., reload',
  regex: 'e.g., ^shutdown.*'
};

const aclModeOptions = [
  {
    label: 'Unrestricted',
    helpText: 'Allow AI Advisor to execute all read-only commands except those explicitly denied by Kentik.',
    value: 'allow_all'
  },
  { label: 'Deny Specific Commands', value: 'deny_specific' },
  { label: 'Allow Specific Commands', value: 'allow_specific' }
];

// Sortable components
const DragHandleComponent = SortableHandle(() => (
  <DragHandle title="Drag to reorder">
    <Icon icon="drag-handle-vertical" size={16} />
  </DragHandle>
));

const SortableRuleItem = SortableElement(
  observer(({ field, onRemove, disableField }) => {
    const matchType = field.matchType?.getValue() || 'exact';

    return (
      <ACLRuleRow>
        {!disableField && <DragHandleComponent />}
        <Field field={field.matchType} showLabel={false} options={matchTypeOptions} m={0} large disabled={disableField}>
          <Select showFilter menuWidth={120} />
        </Field>
        <Field
          field={field.pattern}
          maxWidth={750}
          flex={1}
          showLabel={false}
          placeholder={matchTypePlaceholders[matchType]}
          m={0}
          large
          disabled={disableField}
        >
          <InputGroup maxWidth={750} />
        </Field>
        <Button
          minimal
          icon="trash"
          intent="danger"
          onClick={onRemove}
          aria-label="Remove rule"
          disabled={disableField}
        />
      </ACLRuleRow>
    );
  })
);

const SortableRuleList = SortableContainer(
  observer(({ rulesField, onRemove, disabled }) => (
    <div>
      {rulesField.map((ruleField, index) => {
        const key = ruleField.pattern?._id;
        return (
          <SortableRuleItem
            key={key}
            index={index}
            field={ruleField}
            onRemove={() => onRemove(index)}
            disableField={disabled}
          />
        );
      })}
    </div>
  ))
);

// eslint-disable-next-line react/no-multi-comp
@observer
class DeviceCommandACL extends Component {
  static propTypes = {
    form: PropTypes.object.isRequired,
    disabled: PropTypes.bool.isRequired
  };

  addRule = () => {
    const { form } = this.props;
    const rulesField = form.getField('deviceCommandAcls.rules');
    rulesField.add({ matchType: 'exact', pattern: '' });
  };

  removeRule = (index) => {
    const { form } = this.props;
    const rulesField = form.getField('deviceCommandAcls.rules');
    rulesField.remove(index);
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) {
      return;
    }

    const { form } = this.props;
    const currentRules = form.getValue('deviceCommandAcls.rules') || [];
    const result = Array.from(currentRules);
    const [removed] = result.splice(oldIndex, 1);
    result.splice(newIndex, 0, removed);
    form.setValue('deviceCommandAcls.rules', result);
  };

  renderRules = () => {
    const { form, disabled } = this.props;
    const aclMode = form.getValue('deviceCommandAcls.aclMode') || 'allow_all';
    const rulesField = form.getField('deviceCommandAcls.rules');

    if (aclMode === 'allow_all') {
      return null;
    }

    const ruleTypeLabel = aclMode === 'deny_specific' ? 'Deny' : 'Allow';

    return (
      <RuleContainer gap={1}>
        <Text fontWeight="bold">{ruleTypeLabel} Rules</Text>
        <Text mb={2} color="muted">
          {aclMode === 'deny_specific'
            ? 'Define patterns for commands that will never be executed by AI Advisor, in addition to those explicitly denied by Kentik.'
            : 'Define the exclusive list of permitted command patterns. Everything else is implicitly denied, in addition to those explicitly denied by Kentik.'}
        </Text>

        <SortableRuleList
          rulesField={rulesField}
          onRemove={this.removeRule}
          onSortEnd={this.onSortEnd}
          useDragHandle
          lockAxis="y"
          helperClass="sortableHelper"
          disabled={disabled}
        />

        <Button
          icon="plus"
          minimal
          onClick={this.addRule}
          style={{ alignSelf: 'flex-start', marginTop: '8px' }}
          disabled={disabled}
        >
          Add Rule
        </Button>
      </RuleContainer>
    );
  };

  render() {
    const { disabled } = this.props;
    return (
      <FlexColumn gap={1}>
        <Heading level={3} m={0}>
          Device Command Restrictions
        </Heading>
        <Text as="div" color="muted" mb={2}>
          Apply strict controls over the specific commands that AI Advisor can run via the NMS agent. These controls are
          in addition to several Kentik-managed restrictions that cannot be overridden.
        </Text>

        <Field name="deviceCommandAcls.aclMode" showLabel={false} large options={aclModeOptions} disabled={disabled}>
          <RadioGroup large />
        </Field>

        {this.renderRules()}

        <GlobalAclsPopover>View Kentik-managed restrictions</GlobalAclsPopover>
      </FlexColumn>
    );
  }
}

export default DeviceCommandACL;
